import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiError } from "../lib/auth";
import { Flower2, User, Mail, Lock } from "lucide-react";

const BG_IMG = "https://images.unsplash.com/photo-1603276730862-cbf79a742aae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwzfHxwZWFjZWZ1bCUyMG5hdHVyZSUyMGxhbmRzY2FwZXxlbnwwfHx8fDE3NzY2OTAyMjR8MA&ixlib=rb-4.1.0&q=85";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    setLoading(true);
    try {
      await register(form.name, form.email, form.password);
      navigate("/chapters");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="grid lg:grid-cols-2 min-h-[calc(100vh-4rem)]" data-testid="register-page">
      <div className="flex items-center justify-center p-6 lg:p-12 order-2 lg:order-1">
        <div className="w-full max-w-md animate-fade-up">
          <div className="flex items-center gap-2 mb-8">
            <Flower2 size={32} color="#D96C4A" />
            <span className="font-serif-display text-2xl text-[#2D3B31]">Gītā Sabhā</span>
          </div>
          <h1 className="font-serif-display text-4xl font-medium text-[#2D3B31]">
            Begin your journey
          </h1>
          <p className="mt-2 text-[#5C6B5F]">Every seeker is welcomed with open arms.</p>

          <form onSubmit={onSubmit} className="mt-10 space-y-5" data-testid="register-form">
            <div>
              <label className="text-xs uppercase tracking-widest text-[#8A948C]">Name</label>
              <div className="mt-2 relative">
                <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8A948C]" />
                <input
                  className="input-organic pl-11"
                  placeholder="Your given name"
                  value={form.name}
                  onChange={handle("name")}
                  required
                  data-testid="register-name-input"
                />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#8A948C]">Email</label>
              <div className="mt-2 relative">
                <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8A948C]" />
                <input
                  type="email"
                  className="input-organic pl-11"
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={handle("email")}
                  required
                  data-testid="register-email-input"
                />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#8A948C]">Password</label>
              <div className="mt-2 relative">
                <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8A948C]" />
                <input
                  type="password"
                  className="input-organic pl-11"
                  placeholder="At least 6 characters"
                  value={form.password}
                  onChange={handle("password")}
                  required
                  data-testid="register-password-input"
                />
              </div>
            </div>

            {error && (
              <div className="text-sm text-[#C05B3B] bg-[#F2D8CF]/50 border border-[#F2D8CF] rounded-lg px-4 py-2" data-testid="register-error">
                {error}
              </div>
            )}

            <button type="submit" className="btn-accent w-full" disabled={loading} data-testid="register-submit-btn">
              {loading ? "Creating..." : "Create my account"}
            </button>
          </form>

          <div className="mt-8 text-sm text-[#5C6B5F]">
            Already a seeker?{" "}
            <Link to="/login" className="text-[#D96C4A] hover:underline" data-testid="register-to-login">
              Sign in instead
            </Link>
          </div>
        </div>
      </div>

      <div className="relative hidden lg:block order-1 lg:order-2">
        <img src={BG_IMG} alt="Peaceful nature" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#2D3B31]/40 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="shloka text-xl md:text-2xl">
            श्रद्धावान् लभते ज्ञानम्
          </div>
          <p className="mt-3 text-sm italic font-serif-display opacity-90">
            "The one who has faith, gains wisdom." — Gītā 4.39
          </p>
        </div>
      </div>
    </div>
  );
}
